"use client";

import { useMemo, useState } from "react";
import {
  Button,
  Input,
  IntentBadge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import {
  modelsConfig,
  type ModelSelection,
  type ModelsConfigResponse,
} from "@excellent/ui/api/contracts/models-config";

// The per-role / per-seam model matrix (docs/models/PRD.md §9 / §14): which
// seam runs on which provider+model tier. "Default" keeps today's behavior
// (the CLI path / frontier pin); routing only ever narrows via a promoted,
// gated model — this grid is the operator override, not the router.

export function MatrixSection({
  data,
  onChange,
}: {
  data: ModelsConfigResponse;
  onChange: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, ModelSelection | null>>({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const providerIds = useMemo(
    () => ["claude", ...data.providers.map((p) => p.id).filter((id) => id !== "claude")],
    [data.providers],
  );

  const effective = (seamKey: string): ModelSelection | null =>
    seamKey in draft ? draft[seamKey] ?? null : data.matrix[seamKey] ?? null;

  function setSeam(seamKey: string, sel: ModelSelection | null) {
    setDraft((d) => ({ ...d, [seamKey]: sel }));
  }

  async function save() {
    setSaving(true);
    setNotice(null);
    try {
      await apiCall(modelsConfig.saveMatrix, { body: { matrix: draft } });
      setDraft({});
      setNotice("Matrix saved.");
      onChange();
    } catch {
      setNotice("Could not save the matrix.");
    } finally {
      setSaving(false);
    }
  }

  const groups = useMemo(() => {
    const g = new Map<string, typeof data.seams>();
    for (const s of data.seams) {
      const list = g.get(s.group) ?? [];
      list.push(s);
      g.set(s.group, list);
    }
    return [...g.entries()];
  }, [data.seams]);

  return (
    <SettingsSection
      title="Per-seam model matrix"
      description="Which provider + model each seam runs on. Default = today's behavior (CLI path, frontier fleet pin). Seams marked frontier stay on the frontier model unless explicitly overridden."
    >
      <div>
        {groups.map(([group, seams]) => (
          <div key={group} className="border-b border-border/40 py-3 first:pt-0 last:border-b-0">
            <div className="mb-2 type-subtext text-muted-foreground">{group}</div>
            <div className="space-y-2">
              {seams.map((seam) => {
                const sel = effective(seam.key);
                return (
                  <div key={seam.key} className="flex items-center gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="truncate type-body text-foreground">{seam.label}</span>
                      {seam.frontierDefault ? (
                        <IntentBadge intent="info">stays on frontier</IntentBadge>
                      ) : null}
                    </div>
                    <Select
                      value={sel?.provider ?? "default"}
                      onValueChange={(provider) =>
                        setSeam(
                          seam.key,
                          provider === "default" ? null : { provider, model: sel?.model ?? "" },
                        )
                      }
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default</SelectItem>
                        {providerIds.map((id) => (
                          <SelectItem key={id} value={id}>
                            {id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={sel?.model ?? ""}
                      disabled={!sel}
                      placeholder="model id"
                      className="w-52"
                      onChange={(e) =>
                        sel && setSeam(seam.key, { provider: sel.provider, model: e.target.value })
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Button disabled={saving || Object.keys(draft).length === 0} onClick={() => void save()}>
          {saving ? "Saving…" : "Save matrix"}
        </Button>
        {notice ? <span className="type-subtext text-muted-foreground">{notice}</span> : null}
      </div>
    </SettingsSection>
  );
}
